import { useState, useEffect, useCallback } from 'react';
import votingService, { VoteResponse } from '../services/votingService';

export interface VotingState {
  isLoading: boolean;
  isInitializing: boolean; // Nuevo estado para controlar la carga inicial
  canVote: boolean;
  hasVoted: boolean;
  canSeeMap: boolean | null; // null = indeterminado, true/false = determinado
  isInNYC: boolean;
  requiresLocationPermission: boolean;
  error: string | null;
  location: { latitude: number; longitude: number } | null;
  areaHierarchy: any | null;
  nextVoteTime: string | null;
  reason: string | null;
  lastVote: {
    is_positive: boolean;
    neighborhood_name: string;
  } | null;
}

export interface VotingActions {
  checkVotingStatus: () => Promise<boolean>;
  submitVote: (isPositive: boolean) => Promise<VoteResponse | null>;
  clearError: () => void;
}

const VOTING_STATE_STORAGE_KEY = 'hwa_voting_state_cache_v2';
const VOTING_STATE_MAX_AGE_MS = 1000 * 60 * 60 * 6; // 6h

type PersistedVotingState = Pick<
  VotingState,
  'canVote' | 'hasVoted' | 'canSeeMap' | 'isInNYC' | 'location' | 'areaHierarchy' | 'nextVoteTime' | 'reason' | 'lastVote'
> & {
  updatedAt: number;
};

const readPersistedVotingState = (): PersistedVotingState | null => {
  try {
    const raw = sessionStorage.getItem(VOTING_STATE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedVotingState;
    if (!parsed || typeof parsed.updatedAt !== 'number') return null;
    if (Date.now() - parsed.updatedAt > VOTING_STATE_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const useVoting = (): VotingState & VotingActions => {
  const persisted = readPersistedVotingState();
  const [state, setState] = useState<VotingState>({
    isLoading: false,
    // Si tenemos estado reciente cacheado, evitamos mostrar la pantalla de carga.
    isInitializing: !persisted,
    canVote: persisted?.canVote ?? false,
    hasVoted: persisted?.hasVoted ?? false,
    canSeeMap: persisted?.canSeeMap ?? null,
    isInNYC: persisted?.isInNYC ?? false,
    requiresLocationPermission: false,
    error: null,
    location: persisted?.location ?? null,
    areaHierarchy: persisted?.areaHierarchy ?? null,
    nextVoteTime: persisted?.nextVoteTime ?? null,
    reason: persisted?.reason ?? null,
    lastVote: persisted?.lastVote ?? null
  });

  const checkVotingStatus = useCallback(async (): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: prev.isInitializing, error: null }));
    let success = true;

    try {
      const result = await votingService.canUserVote();

      // Si hay un error transitorio (geolocalización/red), no borrar estado útil previo
      // para evitar ocultar mapa/panel de forma errática.
      if (result.reason === 'location_required') {
        setState(prev => ({
          ...prev,
          isLoading: false,
          isInitializing: false,
          requiresLocationPermission: true,
          canVote: false,
          canSeeMap: false,
          hasVoted: false,
          isInNYC: false,
          location: null,
          areaHierarchy: null,
          reason: result.reason || null,
          error: result.message || 'Location access is required for this app to work.'
        }));
      } else if (result.reason === 'outside_nyc') {
        setState(prev => ({
          ...prev,
          isLoading: false,
          isInitializing: false,
          requiresLocationPermission: false,
          canVote: false,
          // Outside NYC can still browse the map in view-only mode.
          canSeeMap: true,
          hasVoted: true,
          isInNYC: false,
          location: result.location || null,
          areaHierarchy: null,
          reason: result.reason || null,
          error: null
        }));
      } else if (result.reason === 'error') {
        success = false;
        setState(prev => ({
          ...prev,
          isLoading: false,
          isInitializing: false,
          requiresLocationPermission: false,
          error: result.message || null
        }));
      } else {
        setState(prev => ({
          ...prev,
          isLoading: false,
          isInitializing: false, // Marcar como inicialización completa
          requiresLocationPermission: false,
          canVote: result.canVote,
          hasVoted: result.reason === 'already_voted',
          isInNYC: result.reason !== 'outside_nyc',
          location: result.location || null,
          areaHierarchy: result.areaHierarchy || null,
          reason: result.reason || null,
          error: result.message || null
        }));
      }

      // Obtener el estado de voto para obtener nextVoteTime y canSeeMap.
      // En outside_nyc / location_required dejamos el gating resuelto arriba.
      if (result.reason === 'outside_nyc' || result.reason === 'location_required') {
        return success;
      }
      // Obtener el estado de voto para obtener nextVoteTime y canSeeMap
      try {
        const voteStatus = await votingService.getVoteStatus();
        setState(prev => ({
          ...prev,
          canSeeMap: voteStatus.can_see_map || false,
          hasVoted: !voteStatus.can_vote,
          nextVoteTime: voteStatus.next_vote_time || null,
          lastVote: voteStatus.last_vote || null
        }));
      } catch (error) {
        console.warn('Could not get vote status:', error);
        success = false;
      }

      return success;
    } catch (error) {
      console.error('Error checking voting status:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        isInitializing: false, // Marcar como inicialización completa incluso en error
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }));
      return false;
    }
  }, []);

  const submitVote = useCallback(async (isPositive: boolean): Promise<VoteResponse | null> => {
    if (!state.canVote || !state.isInNYC || !state.location) {
      setState(prev => ({ ...prev, error: 'Location not available' }));
      return null;
    }

    // No activar isLoading global aquí para no desmontar el mapa
    // ni volver a mostrar "checking location" tras votar.
    setState(prev => ({ ...prev, error: null }));

    try {
      const result = await votingService.submitVote(
        state.location.latitude,
        state.location.longitude,
        isPositive
      );

      if (result.success) {
        localStorage.setItem('hwa_last_vote_at', new Date().toISOString());
        // Actualizar el estado básico inmediatamente
        setState(prev => ({
          ...prev,
          canVote: false,
          hasVoted: true,
          canSeeMap: true, // Después de votar, puede ver el mapa
          nextVoteTime: result.next_vote_time || null,
          reason: 'already_voted'
        }));
        
        // Obtener el estado de voto actualizado para obtener lastVote
        try {
          const voteStatus = await votingService.getVoteStatus();
          setState(prev => ({
            ...prev,
            lastVote: voteStatus.last_vote || null
          }));
        } catch (error) {
          console.warn('Could not get vote status after voting:', error);
        }
      } else {
        setState(prev => ({
          ...prev,
          error: result.message
        }));
      }

      return result;
    } catch (error) {
      console.error('Error submitting vote:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to submit vote'
      }));
      return null;
    }
  }, [state.canVote, state.isInNYC, state.location]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Verificar estado inicial al montar el componente
  useEffect(() => {
    checkVotingStatus();
  }, [checkVotingStatus]);

  useEffect(() => {
    if (state.canSeeMap === null && !state.location && !state.areaHierarchy) return;
    const payload: PersistedVotingState = {
      canVote: state.canVote,
      hasVoted: state.hasVoted,
      canSeeMap: state.canSeeMap,
      isInNYC: state.isInNYC,
      location: state.location,
      areaHierarchy: state.areaHierarchy,
      nextVoteTime: state.nextVoteTime,
      reason: state.reason,
      lastVote: state.lastVote,
      updatedAt: Date.now()
    };
    try {
      sessionStorage.setItem(VOTING_STATE_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Best effort only.
    }
  }, [
    state.canVote,
    state.hasVoted,
    state.canSeeMap,
    state.isInNYC,
    state.location,
    state.areaHierarchy,
    state.nextVoteTime,
    state.reason,
    state.lastVote
  ]);

  return {
    ...state,
    checkVotingStatus,
    submitVote,
    clearError
  };
};
